import React, { useContext, useMemo } from 'react';
import { FiActivity, FiCheckCircle, FiClock, FiTrendingUp, FiTruck } from 'react-icons/fi';
import { LoadContext } from './loadContext';
import StatCard from './ui/StatCard';
import Surface from './ui/Surface';
import LoadBoard from './LoadBoard';
import Button from './ui/Button';
import styles from './Dashboard.module.css';

const toNumericSeries = (series, keys = []) => {
  if (!Array.isArray(series)) {
    return [];
  }
  return series
    .map((point) => {
      if (typeof point === 'number') {
        return point;
      }
      if (typeof point === 'string') {
        const numeric = Number(point);
        return Number.isFinite(numeric) ? numeric : undefined;
      }
      if (typeof point === 'object' && point !== null) {
        for (const key of keys) {
          const value = point[key];
          if (value !== undefined && value !== null) {
            const numeric = Number(value);
            if (Number.isFinite(numeric)) {
              return numeric;
            }
          }
        }
      }
      return undefined;
    })
    .filter((value) => Number.isFinite(value));
};

const Dashboard = () => {
  const {
    loads = [],
    ftlStats = {},
    costPerMileData = [],
    timeToAcceptanceData = [],
    statsStatus = 'idle'
  } = useContext(LoadContext);

  const {
    loads_this_week: loadsThisWeek,
    loads_week_change_percentage: weekChangePercentage,
    bookings_confirmed: bookingsConfirmed,
    bookings_total: bookingsTotal,
    loads_with_issues: loadsWithIssues,
    on_time_delivery_percentage: onTimeDeliveryPercentage,
  } = ftlStats;

  const activeLoads = useMemo(() => loads.filter((load) => (load.status || '').toLowerCase() !== 'delivered').length, [loads]);
  const deliveredLoads = useMemo(() => loads.filter((load) => (load.status || '').toLowerCase() === 'delivered').length, [loads]);

  const statsAvailable = statsStatus === 'success';
  const bookingValue = statsAvailable && bookingsConfirmed !== undefined && bookingsTotal !== undefined
    ? `${bookingsConfirmed}/${bookingsTotal}`
    : '—';
  const bookingRate = bookingsTotal > 0 ? Math.round((bookingsConfirmed / bookingsTotal) * 100) : 0;

  const averageCostPerMile = useMemo(() => {
    const values = toNumericSeries(costPerMileData, ['value', 'cost', 'amount']);
    if (!values.length) {
      return null;
    }
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    return avg.toFixed(2);
  }, [costPerMileData]);

  const averageAcceptance = useMemo(() => {
    const values = toNumericSeries(timeToAcceptanceData, ['value', 'hours']);
    if (!values.length) {
      return null;
    }
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    return avg.toFixed(1);
  }, [timeToAcceptanceData]);

  const issuesRate = bookingsTotal > 0 ? Math.round((loadsWithIssues / bookingsTotal) * 100) : 0;
  const weekDelta = !statsAvailable
    ? '—'
    : weekChangePercentage === null || weekChangePercentage === undefined
    ? 'No prior-week baseline'
    : `${weekChangePercentage > 0 ? '+' : ''}${weekChangePercentage}%`;
  const weekTone = weekChangePercentage > 0
    ? 'positive'
    : (weekChangePercentage < 0 ? 'negative' : 'neutral');

  return (
    <div className={styles.dashboard}>
      <div className={styles.metricGrid}>
        <StatCard
          title="Loads this week"
          value={statsAvailable ? loadsThisWeek : '—'}
          delta={weekDelta}
          deltaLabel="vs previous calendar week"
          tone={weekTone}
          icon={<FiActivity size={22} />}
        />
        <StatCard
          title="Bookings confirmed"
          value={bookingValue}
          delta={statsAvailable ? `${bookingRate}% confirmed` : '—'}
          deltaLabel="Carrier assigned across active loads"
          tone="neutral"
          icon={<FiTrendingUp size={22} />}
        />
        <StatCard
          title="Loads with issues"
          value={statsAvailable ? loadsWithIssues : '—'}
          delta={statsAvailable ? `${issuesRate}% of active` : '—'}
          deltaLabel="Escalations requiring review"
          tone={issuesRate > 10 ? 'negative' : 'neutral'}
          icon={<FiTruck size={22} />}
        />
        <StatCard
          title="On-time performance"
          value={statsAvailable && onTimeDeliveryPercentage !== null ? `${Math.round(onTimeDeliveryPercentage)}%` : '—'}
          delta={onTimeDeliveryPercentage === null || onTimeDeliveryPercentage === undefined
            ? 'No completed-load sample'
            : (onTimeDeliveryPercentage >= 95 ? 'Service excellence' : 'Needs attention')}
          deltaLabel="Delivered FTL shipments with known results"
          tone={onTimeDeliveryPercentage >= 95 ? 'positive' : 'neutral'}
          icon={<FiCheckCircle size={22} />}
        />
      </div>

      <div className={styles.secondaryGrid}>
        <Surface className={styles.secondaryCard} tone="glass" padding="lg">
          <h3>Network tempo</h3>
          <div className={styles.statList}>
            <div className={styles.statRow}>
              <span>Active loads</span>
              <strong>{activeLoads}</strong>
            </div>
            <div className={styles.statRow}>
              <span>Delivered in last 24h</span>
              <strong>{deliveredLoads}</strong>
            </div>
            <div className={styles.statRow}>
              <span>Avg cost per mile</span>
              <strong>{averageCostPerMile ? `$${averageCostPerMile}` : '—'}</strong>
            </div>
            <div className={styles.statRow}>
              <span>Avg time to acceptance</span>
              <strong>{averageAcceptance ? `${averageAcceptance} hrs` : '—'}</strong>
            </div>
          </div>
        </Surface>
        <Surface className={styles.secondaryCard} tone="glass" padding="lg">
          <h3>Service commitments</h3>
          <div className={styles.statList}>
            <div className={styles.statRow}>
              <span>On-time delivery</span>
              <span className={`${styles.trend} ${onTimeDeliveryPercentage >= 95 ? styles.trendPositive : styles.trendNegative}`}>
                {Number.isFinite(onTimeDeliveryPercentage)
                  ? `${Number(onTimeDeliveryPercentage).toFixed(1)}%`
                  : '—'}
              </span>
            </div>
            <div className={styles.statRow}>
              <span>Escalations awaiting action</span>
              <span className={`${styles.trend} ${issuesRate > 0 ? styles.trendNegative : styles.trendPositive}`}>
                {loadsWithIssues}
              </span>
            </div>
            <div className={styles.statRow}>
              <span>SLAs met (rolling 30d)</span>
              <span className={`${styles.trend} ${onTimeDeliveryPercentage >= 95 ? styles.trendPositive : styles.trendNeutral}`}>
                {Math.min(99, Math.max(80, Math.round(onTimeDeliveryPercentage)))}%
              </span>
            </div>
          </div>
          <Button variant="ghost" size="md" style={{ justifySelf: 'flex-start' }}>
            Review customer health
          </Button>
        </Surface>
      </div>

      <section className={styles.loadBoardSection}>
        <div className={styles.sectionHeading}>
          <div>
            <h2>Live load board</h2>
            <p>Command center visibility across FTL and LTL movements, documents, and milestones.</p>
          </div>
          <Button variant="secondary" leadingIcon={<FiClock size={18} />}>Schedule digest</Button>
        </div>
        <LoadBoard />
      </section>
    </div>
  );
};

export default Dashboard;
